/**
 * Analytics Portal V1 — shared browser client (Phase 4, plan §13.3 C16 / approval §11-12).
 *
 * Rules this module enforces so no call site has to think about them:
 *   - Nothing is ever sent while collection is disabled. Search/directory events carry a
 *     server-signed token, whose mere existence proves collection is on; the tokenless
 *     click events resolve `collection_enabled` through ONE lazy, in-memory-cached
 *     `GET /api/analytics/config` the first time an instrumented control is used.
 *   - In-memory queue only. No localStorage, no sessionStorage, no IndexedDB, no cookies.
 *   - Batches are capped at 20 events (the server accepts 25) with a bounded flush delay.
 *   - Delivery is `navigator.sendBeacon` with a single `fetch(keepalive)` fallback:
 *     fire-and-forget, never awaited, never retried, never blocking navigation.
 *   - Never throws into the UI, never logs a payload, never inspects cookies.
 *   - The client never submits dims, visitor/session/actor ids, source, referrer, raw
 *     query text, full URLs, emails, phone numbers, names or free-form metadata.
 */
import { API_BASE } from "@/lib/api";

const COLLECT_URL = `${API_BASE}/analytics/collect`;
const CONFIG_URL = `${API_BASE}/analytics/config`;

export const MAX_BATCH = 20;
const MAX_QUEUE = 60;
const FLUSH_DELAY_MS = 1500;

// Exactly the keys the server tells the client to echo back for a signed result.
const RESULT_KEYS = ["token", "entity_type", "entity_id", "position", "search_id",
  "listing_id", "surface", "directory"];

let collecting = null;      // null = not resolved yet, true / false = resolved
let configRequest = null;
let queue = [];
let timer = null;
let lifecycleBound = false;

const browser = () => typeof window !== "undefined" && typeof document !== "undefined";

function resolveCollecting() {
  if (collecting !== null) return Promise.resolve(collecting);
  if (!configRequest) {
    configRequest = fetch(CONFIG_URL, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { collecting = Boolean(d && d.collection_enabled); return collecting; })
      .catch(() => { collecting = false; return false; });
  }
  return configRequest;
}

function deliver(body) {
  try {
    if (navigator.sendBeacon
        && navigator.sendBeacon(COLLECT_URL, new Blob([body], { type: "application/json" }))) {
      return;
    }
  } catch (_) { /* fall through to the keepalive fetch */ }
  try {
    fetch(COLLECT_URL, {
      method: "POST", body, keepalive: true, credentials: "include",
      headers: { "Content-Type": "application/json" },
    }).catch(() => {});
  } catch (_) { /* delivery is best-effort only */ }
}

function send() {
  while (queue.length) {
    const batch = queue.splice(0, MAX_BATCH);
    deliver(JSON.stringify({ events: batch }));
  }
}

function flush() {
  if (timer !== null) { clearTimeout(timer); timer = null; }
  if (!queue.length) return;
  if (collecting === false) { queue = []; return; }
  if (collecting === null) {
    resolveCollecting().then((on) => { if (on) send(); else queue = []; }).catch(() => {});
    return;
  }
  send();
}

function bindLifecycle() {
  if (lifecycleBound || !browser()) return;
  lifecycleBound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", () => flush());
}

function enqueue(event) {
  queue.push(event);
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
  bindLifecycle();
  if (queue.length >= MAX_BATCH) { flush(); return; }
  if (timer === null) timer = setTimeout(() => { timer = null; flush(); }, FLUSH_DELAY_MS);
}

const clientTs = () => new Date().toISOString();

/** Host of an http(s) URL, lowercased. Never a path, query, mailto or tel value. */
export function hostOf(url) {
  try {
    if (!url) return null;
    const u = new URL(url, browser() ? window.location.href : undefined);
    return /^https?:$/.test(u.protocol) ? u.hostname.toLowerCase() : null;
  } catch (_) {
    return null;
  }
}

/** Collapsed, length-bounded label taken from an existing control's own text. */
export function boundedLabel(text) {
  const label = String(text || "").replace(/\s+/g, " ").trim();
  return label ? label.slice(0, 80) : null;
}

/**
 * Search / directory impression or click. `analytics` is the additive object the server
 * attached to the result — it is echoed verbatim and re-validated against the signed
 * token at collect time. No token means analytics is off: silently do nothing.
 */
export function trackResult(eventName, analytics) {
  try {
    if (!browser() || !analytics || !analytics.token || collecting === false) return;
    if (collecting === null) collecting = true;   // a signed token proves collection is on
    const event = { event_name: eventName, client_ts: clientTs() };
    RESULT_KEYS.forEach((key) => {
      if (analytics[key] !== undefined && analytics[key] !== null) event[key] = analytics[key];
    });
    enqueue(event);
  } catch (_) { /* analytics never breaks the UI */ }
}

/** A tokenless click on an existing control (CTA, resource visit, outbound/internal link). */
export function trackAction(eventName, { entityType, entityId, meta } = {}) {
  try {
    if (!browser() || !eventName || !entityType || !entityId || collecting === false) return;
    const event = { event_name: eventName, entity_type: entityType, entity_id: entityId,
      client_ts: clientTs() };
    Object.entries(meta || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") event[key] = value;
    });
    enqueue(event);
    if (collecting === null) resolveCollecting().catch(() => {});
  } catch (_) { /* analytics never breaks the UI */ }
}

export const trackOutboundLink = (entityType, entityId, linkKind, url) =>
  trackAction("outbound_link_clicked", {
    entityType, entityId, meta: { link_kind: linkKind, destination_host: hostOf(url) },
  });

export const trackInternalLink = (entityType, entityId, linkKind, targetType, targetId) =>
  trackAction("internal_link_clicked", {
    entityType, entityId,
    meta: { link_kind: linkKind, target_type: targetType, target_id: targetId },
  });

/** Test-only hook: drops every in-memory bit of state. Never called by the app. */
export function __resetAnalyticsForTests() {
  collecting = null;
  configRequest = null;
  queue = [];
  if (timer !== null) { clearTimeout(timer); timer = null; }
}
